import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureDataLakeStoreCosmosStructuredStreamFileDataset } from 'app/model/dataset/azure-data-lake-store-cosmos-structured-stream.dataset';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { DatasetAdapter } from 'app/model/adapters/datasets/dataset.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDataLakeStoreCosmosStructuredStreamFileDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            structure: '*',
            schema: '*',
            linkedServiceName: '*',
            parameters: '*',
            type: '*',
            typeProperties: {
                folderPath: '*',
                fileName: '*',
                generatedFromActivity: '*'
            }
        }
    }
})

// Note - This class is the adapter for the Azure Data Lake Store - Cosmos dataset type that is to serve first party customers.
// Scoping out the changes to a separate adapter class despite being similar to AzureDataLakeStoreFileDatasetAdapter, to avoid changes to existing
// Azure Data Lake Store dataset and its model and adapters for minimal risk impact.
// TODO: Unify with the existing Azure Data Lake Store dataset once the first party needs are thouroughly tested and verified.
export class AzureDataLakeStoreCosmosStructuredStreamFileDatasetAdapter extends DatasetAdapter<AzureDataLakeStoreCosmosStructuredStreamFileDataset, Model.DatasetResource> {
    protected async _toResource(dataset: AzureDataLakeStoreCosmosStructuredStreamFileDataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.AzureDataLakeStoreCosmosStructuredStreamDataset;
        modelDataset.type = Metamodel.modelFor(AzureDataLakeStoreCosmosStructuredStreamFileDataset).name;
        if (dataset.isV2) {
            modelDataset.schema = this.columnValueToResource(dataset, dataset.columns);
        } else {
            modelDataset.structure = this.columnValueToResource(dataset, dataset.columns);
        }
        modelDataset.typeProperties = {
            fileName: ValueAdapter.toResource(dataset, AzureDataLakeStoreCosmosStructuredStreamFileDataset.FILE_NAME),
            folderPath: ValueAdapter.toResource(dataset, AzureDataLakeStoreCosmosStructuredStreamFileDataset.FOLDER_PATH),
            generatedFromActivity: dataset.generatedFromActivity
        };
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetResource, dataset?: AzureDataLakeStoreCosmosStructuredStreamFileDataset): Promise<AzureDataLakeStoreCosmosStructuredStreamFileDataset> {
        if (!dataset) {
            dataset = AzureDataLakeStoreCosmosStructuredStreamFileDataset.MODEL.create<AzureDataLakeStoreCosmosStructuredStreamFileDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        const model = modelDatasetResource.properties as Model.AzureDataLakeStoreCosmosStructuredStreamDataset;
        if (model) {
            if (model.typeProperties != null) {
                ValueAdapter.toModel(dataset, AzureDataLakeStoreCosmosStructuredStreamFileDataset.FILE_NAME, model.typeProperties.fileName);
                ValueAdapter.toModel(dataset, AzureDataLakeStoreCosmosStructuredStreamFileDataset.FOLDER_PATH, model.typeProperties.folderPath);
                dataset.generatedFromActivity = model.typeProperties.generatedFromActivity;
            }
            dataset.columns = this.columnResourceToValue(dataset, model.structure);
            dataset.isV2 = model.schema != null;
            const cols = dataset.isV2 ? model.schema : model.structure;
            if (!dataset.isV2) { // v1
                dataset.columns = this.columnResourceToValue(dataset, cols);
            } else if (Array.isArray(cols)) { // v2
                dataset.columns = this.columnResourceToValue(dataset, cols);
                dataset.columns.forEach((c) => {
                    c.physicalType = c.columnType;
                });
            }
        }
        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, AzureDataLakeStoreCosmosStructuredStreamFileDataset.MODEL));
        return dataset;
    }
}
